import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";
import TabControl from "../TabControl";

import { V2 } from "../../Api";
import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, truncateEllipse } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface ISurgeryReviewViewState {
    isLoading: boolean | string;

    appointments: any[];
    dateMode: "for" | "on";

    locations: any[];
    searchText: string;
    searchLocation: string;
    searchOffset: number;
    // searchPerson: string;
    searchStatus: string;

    searchEnd: string;
    searchStart: string;

    selectedTab: number;

    [name: string]: any;
}

const PAGE_SIZE = 50;

class SurgeryReviewView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, ISurgeryReviewViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const yesterday = DateTime.local().startOf("day").minus({ day: 1 });

        this.state = {
            appointments: [],
            dateMode: props.location.state ? props.location.state.dateMode : "on",

            locations: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchText: props.location.state ? props.location.state.searchText : "",

            searchEnd: props.location.state ? props.location.state.searchEnd : yesterday.toISODate(),
            searchStart: props.location.state ? props.location.state.searchStart : yesterday.toISODate(),

            isLoading: true,

            selectedTab: props.location.state ? props.location.state.selectedTab : 0,
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await V2.get<any[]>("locations?type=surgery");

            this.setState({
                locations
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.review && (this.state.selectedTab === 1 ? a.review.finalReviewStatus : (a.review.finalReviewStatus || a.review.firstReviewStatus)))}>
                        <td data-caption="Scheduled Date: ">{formatDateTimeLocalNumeric(a.creationTimestamp)}</td>
                        <td data-caption="Name: " className="wrap">{formatName(a.patient.name)}<br /><small>({a.patient.chartNumber})</small></td>
                        <td data-caption="Type: ">{a.primarySurgery.shortName}</td>
                        <td data-caption="Provider: " className="wrap">{formatName(a.provider.name)}</td>
                        <td data-caption="Scheduled For: ">{a.time ? formatDateTimeLocalNumeric(DateTime.fromISO(`${a.date.substring(0, 10)}T${a.time}`, { zone: "America/New_York" })) : formatDateLocalNumeric(Date.fromLocalDate(a.date.substring(0, 10)))}</td>
                        <td data-caption="Note: " className="wrap col-400-max">{truncateEllipse(a.note, 60)}</td>
                        <td>
                            <Link className="button small" to={`/surgery/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Created On</th>
                            <th>Name</th>
                            <th>Surgery</th>
                            <th>Provider</th>
                            <th>Scheduled For</th>
                            <th>Note</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Surgery Appointments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <select name="dateMode" value={this.state.dateMode} onChange={this.handleElementChange}>
                        <option value="for">Scheduled For</option>
                        <option value="on">Created On</option>
                    </select>
                    <div className="control-group fill-space">
                        <span>Start Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <div className="control-group fill-space">
                        <span>End Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="good">Good to go</option>
                        <option value="issue">Issue</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                    {/*<select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                    </select>*/}
                </div>
                <TabControl headers={["Added yesterday", "1 day", "3 days", "All"]} defaultSelectedIndex={this.state.selectedTab} onTabChanged={this.handleTabChanged} />
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=overview",
                `date=${this.state.dateMode}`,
                "status=status_good"
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`review_status=${this.state.searchStatus}`);
            }
            if (this.state.searchEnd) {
                const beforeDt = DateTime.fromISO(this.state.searchEnd).endOf("day").plus({ millisecond: 1 });
                parameters.push(`before=${beforeDt.toISODate()}`);
            }
            if (this.state.searchStart) {
                parameters.push(`after=${this.state.searchStart}`);
            }

            const results = await V2.get<any[]>(`surgery/summary?${parameters.join("&")}`);
            for (const item of results) {
                item.note = (item.note || "").replace(/[\r\n]+/g, " ");
            }

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 2: return "row-yellow";
            case 1: return "row-green";
            case 0:
            default: return "";
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchLocation: this.state.searchLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,

            searchEnd: this.state.searchEnd,
            searchStart: this.state.searchStart,
            selectedTab: this.state.selectedTab,
            dateMode: this.state.dateMode
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }

    private handleTabChanged(index: number) {
        const data: Partial<ISurgeryReviewViewState> = {
            selectedTab: index
        };

        switch (index) {
            case 0: {
                // Added yesterday
                data.dateMode = "on";
                const date = DateTime.local().startOf("day").minus({ day: 1 });
                data.searchStart = date.toISODate();
                data.searchEnd = date.toISODate();
            } break;
            case 1: {
                // Cases happening tomorrow
                data.dateMode = "for";
                const date = DateTime.local().startOf("day").plus({ day: 1 });
                data.searchStart = date.toISODate();
                data.searchEnd = date.toISODate();
            } break;
            case 2: {
                // Cases 3 days from now
                data.dateMode = "for";
                const date = DateTime.local().startOf("day").plus({ day: 3 });
                data.searchStart = date.toISODate();
                data.searchEnd = date.toISODate();
            } break;
            case 3: // All
                data.dateMode = "on";
                data.searchStart = data.searchEnd = undefined;
                break;
            default:
                break;
        }

        this.setState(data, () => this.search());
    }
}

export default withAuth(withTitle("Surgery Review", SurgeryReviewView));
